import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import Icon from '@mlb/react-patterns/v4/components/Icon/icon';
import Link from '@mlb/react-patterns/v4/components/Link/link';
import COLORS from '@mlb/react-patterns/v4/style/universal.color';

import getTranslation from 'bdata-sns-shared/utils/getTranslation';

import {
    WhereToWatchModal,
    ButtonsWrapper,
    Header,
    CloseButton,
    PartnerButton,
    AudioButton,
    PartnerLink,
    ContentRestrictionBody
} from './WhereToWatch.style';

import { FGOTD_STATUS_CODE } from '../../../utils/getWhereToWatchData';
const { mlbBlack, mlbWhite } = COLORS;

const backdropProps = {
    variants: {
        show: {
            opacity: 0.5
        }
    },
    style: {
        opacity: 0.5
    }
};

const contentProps = {
    isCentered: true,
    style: {
        borderRadius: '20px',
        background: mlbWhite,
        width: '400px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: '100%' // Responsive max-width
    }

};

const WhereToWatch = ({
    isOpen,
    setIsOpen,
    watchData,
    dictionary,
    tvLink,
    audioLink
}) => {
    const handleClose = useCallback(() => {
        setIsOpen(false);
    }, [setIsOpen]);
    const handlers = useMemo(() => ({
        onClick: handleClose
    }), [handleClose]);

    const { contentRestriction = {}, status } = watchData ?? {};
    const { availablePartners = [], contentRestrictionDetail = {} } = contentRestriction;
    const { title = '', body = {} } = contentRestrictionDetail;
    const availablePartnerTitles = availablePartners
        .filter(({ partnerTitle }) => partnerTitle && !title.includes(partnerTitle))
        .map(({ partnerTitle }) => partnerTitle);

    const partnerString = availablePartnerTitles.length > 0 ? ' ' + availablePartnerTitles.join(', ') : '';
    const fgotdLabel = `${getTranslation(dictionary, 'button_watch', 'Watch')} ${getTranslation(dictionary, 'freegameoftheday', 'Free Game of the Day')}`;

    // If there is only one availablePartner or it's the Free Game of the Day, we don't need to append the partner titles
    const modalTitle = availablePartners.length === 1 || status === FGOTD_STATUS_CODE ? title : `${title}${partnerString}`;
    return (
        <WhereToWatchModal
            isOpen={isOpen}
            isCentered={true}
            onClose={handleClose}
            backdropProps={backdropProps}
            contentProps={contentProps}
            hideCloseButton={true}>
            <CloseButton
                handlers={handlers}
                shape='solid'
                size="sm"
                styleName='default'>
                <Icon name='close' fill={mlbBlack} width='16' />
            </CloseButton>
            <Header>{modalTitle}</Header>
            <ButtonsWrapper>
                <ContentRestrictionBody dangerouslySetInnerHTML={{ __html: body.html }} />
                {
                    availablePartners.map(({ partnerTitle, deepLink }) => {
                        const buttonLabel = `${getTranslation(dictionary, 'open', 'Open')} ${partnerTitle}`;
                        return (
                            <PartnerLink key={partnerTitle} url={deepLink?.link} className='wheretowatch-partner-link'>
                                <PartnerButton>{buttonLabel}</PartnerButton>
                            </PartnerLink>
                        );
                    })}
                {
                    (status === FGOTD_STATUS_CODE && tvLink) && (
                        <PartnerLink key='fgotd' url={tvLink} className='wheretowatch-fgotd-link'>
                            <PartnerButton>{fgotdLabel}</PartnerButton>
                        </PartnerLink>
                    )
                }

                {
                    audioLink && (
                        <Link url={audioLink} className='wheretowatch-audio-link'>
                            <AudioButton>{getTranslation(dictionary, 'button_mlb_audio', 'Listen to MLB Audio')}</AudioButton>
                        </Link>
                    )
                }
            </ButtonsWrapper>
        </WhereToWatchModal>
    );
};

WhereToWatch.displayName = 'WhereToWatch';

WhereToWatch.propTypes = {
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    watchData: PropTypes.object,
    dictionary: PropTypes.object,
    tvLink: PropTypes.string,
    audioLink: PropTypes.string
};

export default WhereToWatch;
